import { utcDayBounds } from '@/lib/calendarDay';

/** Counts how many occurrences of a (possibly recurring) booking start strictly before today.
 *  Used only for the past-date warning copy. Weekly/daily/monthly expansion, bounded at today
 *  and at any `until`. A non-recurring past booking counts as 1.
 *
 *  Floors to UTC midnight (not local): the calendar is UTC-anchored end to end (a booking's stored
 *  `date` is UTC-midnight of the centre's day — see @/lib/calendarDay), so "today" and the
 *  day-stepping below must both stay on the UTC calendar day. A local floor put a traveller west of
 *  UTC on the wrong calendar day near midnight, over- or under-counting today's booking. The cursor
 *  advances via setUTCDate/setUTCMonth (not local setDate/setMonth) so DST transitions in the
 *  runner's local TZ can never nudge the count. See isPastDateBooking for the same fix. */
export function countPastOccurrences(
  genesis: Date,
  recurrence: { frequency?: string | null; interval?: number | null; until?: Date | string | null } | null,
  today: Date = new Date(),
): number {
  const start = utcDayBounds(genesis).start;
  const todayMid = utcDayBounds(today).start;
  if (start.getTime() >= todayMid.getTime()) return 0;

  if (!recurrence || !recurrence.frequency) return 1;

  const interval = recurrence.interval && recurrence.interval > 0 ? recurrence.interval : 1;
  const until = recurrence.until ? utcDayBounds(new Date(recurrence.until)).start : null;

  let count = 0;
  const cursor = new Date(start);
  for (let i = 0; i < 520; i++) {
    if (cursor.getTime() >= todayMid.getTime()) break;
    if (until && cursor.getTime() > until.getTime()) break;
    count++;
    if (recurrence.frequency === 'daily') cursor.setUTCDate(cursor.getUTCDate() + interval);
    else if (recurrence.frequency === 'weekly') cursor.setUTCDate(cursor.getUTCDate() + 7 * interval);
    else if (recurrence.frequency === 'monthly') cursor.setUTCMonth(cursor.getUTCMonth() + interval);
    else break;
  }
  return count;
}
